import { React, useEffect, useState, useRef } from 'react';
import { Link as RouterLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    subMonths,
    subYears,
    format
} from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    LabelList,
    PieChart,
    Pie,
    Cell,
    Legend
} from "recharts";
// material
import {
    Card,
    IconButton,
    Stack,
    Button,
    TextField,
    Divider,
    FormControl,
    MenuItem,
    InputLabel, Select,
    Autocomplete,
    Tooltip,
    Box,
    Typography,
    InputAdornment,
    CardContent,
    Grid,
    Paper,
    FormLabel,
    List,
    ListItemButton,
    Popper,
    ClickAwayListener
} from '@mui/material';
import { Icon } from '@iconify/react';
import Moment from 'moment';
import Iconify from '../../Iconify';
import LoadingProgressBar from '../../../theme/overrides/LoadingProgressBar';
import URLS from '../../../services/urls.service';
import Page from '../../Page';
import AppCache from '../../../services/AppCache';
import StickyHeader from '../../Common/StickyHeader';


// ----------------------------------------------------------------------



// ----------------------------------------------------------------------

export default function IssueStatistics() {
    const monthDdl = [
        { Id: 1, value: 'Current Month' },
        { Id: 2, value: 'Previous Month' },
        { Id: 3, value: 'Current Year' },
        { Id: 4, value: 'Previous Year' },
        { Id: 5, value: 'Define Date' },
    ];
    const monthWiseDdl = [
        { Id: 0, value: 'Current Year' },
        { Id: 1, value: 'Previous Year' },
    ];
    const CurrentLoggedInUser = AppCache.GetCurrentLoggedInUser();
    const userId = CurrentLoggedInUser?.ExecutiveId;

    // dropdown
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [issueDetailsData, setIssueDetailsData] = useState([]);
    const [isLoading, setIsLoading] = useState();

    const location = useLocation();

    const [site, setSite] = useState("");
    const [selectedSite, setSelectedSite] = useState([]);
    const [clientId, setClientId] = useState(0);
    const [isAllDistributor, setIsAllDistributor] = useState(true);

    const [issueDetailsDDl, setIssueDetailsDDl] = useState(monthDdl[0]);
    const [showDatePickers, setShowDatePickers] = useState(false);
    const [fromDate, setFromDate] = useState(startOfMonth(new Date()));
    const [toDate, setToDate] = useState(endOfMonth(new Date()));

    const [displayLabel, setDisplayLabel] = useState(monthDdl[0].value);
    const [open, setOpen] = useState(false);
    const [lagInterval, setLagInterval] = useState("");
    const [pendingLagData, setPendingLagData] = useState([]);

    const [category, setCategory] = useState(monthDdl[0]);
    const [status, setStatus] = useState('');
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [showCategoryDatePickers, setShowCategoryDatePickers] = useState(false);
    const [fromDateCategory, setFromDateCategory] = useState('' || startOfMonth(new Date()));
    const [toDateCategory, setToDateCategory] = useState('' || endOfMonth(new Date()));
    const [displayLabelCategory, setDisplayLabelCategory] = useState(monthDdl[0].value);
    const [openCategory, setOpenCategory] = useState(false);
    const [hasCategoryData, setHasCategoryData] = useState(true);

    const [monthDate, setMonthDate] = useState(0);
    const [monthWiseData, setMonthWiseData] = useState([]);

    const allDistributorOption = { SiteId: 0, Name: "All Distributor", ClientId: 0 };
    const options = [allDistributorOption, ...selectedSite];

    const handleSite = (newValue) => {
        if (!newValue || newValue.SiteId === 0) {
            // No selection = All Distributor
            setSite(0);
            setClientId(0);
            setIsAllDistributor(true);
            return;
        }

        // Specific distributor selected
        setSite(newValue.SiteId);
        setClientId(newValue.ClientId);
        setIsAllDistributor(false);
    };

    const handleIssueDetailsDdl = (newValue) => {
        if (!newValue?.Id) return;

        setIssueDetailsDDl(newValue);

        setShowDatePickers(newValue.Id === 5);
        setOpen(newValue.Id === 5);
        setDisplayLabel(newValue?.value ?? "");

        const today = new Date();

        switch (newValue.Id) {
            case 1: { // Current Month
                setFromDate(
                    format(startOfMonth(today), "dd-MMM-yyyy")
                );
                setToDate(
                    format(endOfMonth(today), "dd-MMM-yyyy")
                );
                break;
            }

            case 2: { // Previous Month
                const prevMonth = subMonths(today, 1);
                setFromDate(
                    format(startOfMonth(prevMonth), "dd-MMM-yyyy")
                );
                setToDate(
                    format(endOfMonth(prevMonth), "dd-MMM-yyyy")
                );
                break;
            }

            case 3: { // Current Year
                setFromDate(
                    format(startOfYear(today), "dd-MMM-yyyy")
                );
                setToDate(
                    format(endOfYear(today), "dd-MMM-yyyy")
                );
                break;
            }

            case 4: { // Previous Year
                const prevYear = subYears(today, 1);
                setFromDate(
                    format(startOfYear(prevYear), "dd-MMM-yyyy")
                );
                setToDate(
                    format(endOfYear(prevYear), "dd-MMM-yyyy")
                );
                break;
            }

            case 5: { // Define Date
                setFromDate(null);
                setToDate(null);
                break;
            }

            default:
                break;
        }
    };

    const handleApplyDate = () => {
        if (!fromDate || !toDate) return;

        setDisplayLabel(
            `${format(fromDate, "dd/MM/yyyy")} - ${format(toDate, "dd/MM/yyyy")}`
        );

        setOpen(false);
    };

    const handleResetDate = () => {
        const today = new Date();

        setFromDate(null);
        setToDate(null);
        setDisplayLabel(monthDdl[0]?.value || "");
        setShowDatePickers(false);
        setIssueDetailsDDl(monthDdl[0] || null);
        setFromDate(format(startOfMonth(today), "dd-MMM-yyyy"));
        setToDate(format(endOfMonth(today), "dd-MMM-yyyy"));
    };

    const handleIssueDetailsFilter = () => {
        const payload = {
            UserId: String(userId),
            FromDate: Moment(fromDate).format('DD-MMM-YYYY'),
            ToDate: Moment(toDate).format('DD-MMM-YYYY'),
            ClientId: clientId,
            SiteId: String(site),
            IsAllDistributors: isAllDistributor
        };

        setIsLoading(true);

        URLS.getCountOfIssuesByClient(payload)
            .then((response) => {
                setIssueDetailsData(response.data);
            })
            .catch((error) => {
                console.log("error", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleLagInterval = (e) => {
        setLagInterval(e.target.value)
    };

    const handlePendingGraphFilter = () => {
        const payload = {
            UserId: String(userId),
            ClientId: clientId,
            SiteId: String(site),
            IsAllDistributors: isAllDistributor,
            LagDays: lagInterval || "7,15,30",
        };

        setIsLoading(true);

        URLS.getPendingIssueLagDaysByClient(payload)
            .then((response) => {
                setPendingLagData(response.data);
            })
            .catch((error) => {
                console.log("error", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleStatus = (newValue) => {
        setStatus(newValue?.Id || "");
    };

    const handleCategoryDDL = (newValue) => {
        if (!newValue?.Id) return;

        setCategory(newValue);

        setShowCategoryDatePickers(newValue.Id === 5);
        setOpenCategory(newValue.Id === 5);
        setDisplayLabelCategory(newValue?.value ?? "");

        const today = new Date();

        switch (newValue.Id) {
            case 1: { // Current Month
                setFromDateCategory(
                    format(startOfMonth(today), "dd-MMM-yyyy")
                );
                setToDateCategory(
                    format(endOfMonth(today), "dd-MMM-yyyy")
                );
                break;
            }

            case 2: { // Previous Month
                const prevMonth = subMonths(today, 1);
                setFromDateCategory(
                    format(startOfMonth(prevMonth), "dd-MMM-yyyy")
                );
                setToDateCategory(
                    format(endOfMonth(prevMonth), "dd-MMM-yyyy")
                );
                break;
            }

            case 3: { // Current Year
                setFromDateCategory(
                    format(startOfYear(today), "dd-MMM-yyyy")
                );
                setToDateCategory(
                    format(endOfYear(today), "dd-MMM-yyyy")
                );
                break;
            }

            case 4: { // Previous Year
                const prevYear = subYears(today, 1);
                setFromDateCategory(
                    format(startOfYear(prevYear), "dd-MMM-yyyy")
                );
                setToDateCategory(
                    format(endOfYear(prevYear), "dd-MMM-yyyy")
                );
                break;
            }

            case 5: { // Define Date
                setFromDateCategory(null);
                setToDateCategory(null);
                break;
            }

            default:
                break;
        }
    };

    const handleCategoryApplyDate = () => {
        if (!fromDateCategory || !toDateCategory) return;

        setDisplayLabelCategory(
            `${format(fromDateCategory, "dd/MM/yyyy")} - ${format(toDateCategory, "dd/MM/yyyy")}`
        );

        setOpenCategory(false);
    };

    const handleCategoryResetDate = () => {
        const today = new Date();

        setFromDateCategory(null);
        setToDateCategory(null);
        setDisplayLabelCategory(monthDdl[0]?.value || "");
        setShowCategoryDatePickers(false);
        setCategory(monthDdl[0] || null);
        setFromDateCategory(format(startOfMonth(today), "dd-MMM-yyyy"));
        setToDateCategory(format(endOfMonth(today), "dd-MMM-yyyy"));
    };

    console.log(status, "status")
    const handleCategoryWiseFilter = () => {
        const payload = {
            UserId: String(userId),
            FromDate: Moment(fromDateCategory).format('DD-MMM-YYYY'),
            ToDate: Moment(toDateCategory).format('DD-MMM-YYYY'),
            ClientId: clientId,
            SiteId: String(site),
            IsAllDistributors: isAllDistributor,
            IsAllStatus: !(status > 0),
            Status: String(status)
        };

        setIsLoading(true);

        URLS.getCategoryWiseIssue(payload)
            .then((response) => {
                const data = response?.data ?? [];
                setCategoryData(data);
                setHasCategoryData(Array.isArray(data) && data.length > 0);
            })
            .catch((error) => {
                console.log("error", error);
                setCategoryData([]);
                setHasCategoryData(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleMonthDate = (newValue) => {
        setMonthDate(newValue?.Id ?? 0);
    };

    const handleMonthWiseFilter = () => {
        const currentYear = new Date().getFullYear();
        const selectedYear = monthDate === 0 ? currentYear : currentYear - 1;

        const payload = {
            UserId: String(userId),
            FromDate: `01-Jan-${selectedYear}`,
            ToDate: `31-Dec-${selectedYear}`,
            ClientId: clientId,
            SiteId: String(site),
            IsAllDistributors: isAllDistributor,
        };

        setIsLoading(true);

        URLS.getMonthWiseTotalIssue(payload)
            .then((response) => {
                setMonthWiseData(response.data);
            })
            .catch((error) => {
                console.log("error", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleClick = () => {
        navigate('/dashboard/issuetracker')
    }

    const cardsData = [
        {
            id: 1,
            bg: "#EFF6FF",
            title: "Total Issues",
            icon: 'material-symbols:article-outline',
            iconColor: '#2B7FFF',
            count: issueDetailsData[0]?.TotalIssueCount || 0,
            subTxt: 'All issues that has been created'
        },
        {
            id: 2,
            bg: "#FFF7ED",
            title: "Pending issues",
            icon: 'material-symbols:schedule-outline-rounded',
            iconColor: '#F54A00',
            count: issueDetailsData[0]?.PendingIssueCount || 0,
            subTxt: 'Issues awaiting actions'
        },
        {
            id: 3,
            bg: "#E3FFED",
            title: "Completed Issues",
            icon: 'material-symbols:check-circle-outline',
            iconColor: '#00C951',
            count: issueDetailsData[0]?.ClosedIssueCount || 0,
            subTxt: 'Issues successfully resolved'
        },
        {
            id: 4,
            bg: "#F5F5F5",
            title: "On - hold issues",
            icon: 'material-symbols:motion-photos-paused-outline-rounded',
            iconColor: '#6A7282',
            count: issueDetailsData[0]?.OnHoldIssueCount || 0,
            subTxt: 'Issues temporarily paused'
        }
    ];

    const lagData = pendingLagData?.map(item => ({
        lag: item.LagSlab,
        count: item.PendingIssueCount
    }));

    const colors = [
        "#5470E3",
        "#FF9F43",
        "#FFD54F",
        "#A8CF2A",
        "#4B4F6B",
        "#00A6D6"
    ];

    const categoryWiseData = categoryData?.map((item, index) => ({
        name: item.IssueCategoryName,
        value: item.IssueCount,
        color: colors[index % colors.length] // auto-assign colors
    }));

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const monthData = Array.isArray(monthWiseData)
        ? months.map((month, index) => {
            const apiItem = monthWiseData.find(
                item => Number(item?.MonthNo) === index + 1
            );

            return {
                month,
                count: Number(apiItem?.IssueCount) || 0,
            };
        })
        : months.map(month => ({
            month,
            count: 0,
        }));


    console.log("monthWiseData", monthWiseData);

    const chartYear = monthWiseData?.[0]?.Year || "";

    useEffect(() => {
        URLS.getClientWiseDistributorDDL({ userId })
            .then((response) => {
                setSelectedSite(Array.isArray(response.data) ? response.data : []);
            })
            .catch(() => {
                console.log("Error");
            });
        URLS.loadStatusDropDown()
            .then((response) => {
                console.log("res", response)
                setSelectedStatus(Array.isArray(response.data) ? response.data : []);
            })
            .catch((error) => {
                console.error("Error loading tracker data:");
            });
        URLS.getIssueTrackerDashboardDefaultLagDays()
            .then((response) => {
                const value = response?.data?.[0]?.Value || "";
                setLagInterval(value);
            })
            .catch((error) => {
                console.error("Error loading tracker data:");
            });
    }, []);

    useEffect(() => {
        handleIssueDetailsFilter();
        handlePendingGraphFilter();
        handleCategoryWiseFilter();
        handleMonthWiseFilter();
    }, [site]);


    const anchorRef = useRef(null);
    const anchorCategoryRef = useRef(null);
    return (

        <Page title="Issue Statistics" >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1,
                    mx: 3
                }}
            >
                {/* Left aligned text */}
                <Box>
                    <Typography sx={{ fontSize: '22px', fontWeight: 600 }}>
                        Issue Statistics
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#94A3B8' }}>
                        Overview of Issue Summary and Current Statistics
                    </Typography>
                </Box>

                {/* Right aligned Autocomplete */}
                <FormControl size="small" sx={{ minWidth: 260 }}>
                    <Autocomplete
                        size="small"
                        id="distributor"
                        value={options.find((item) => item.SiteId === site) || allDistributorOption}
                        onChange={(_, newValue) => handleSite(newValue)}
                        options={options}
                        getOptionLabel={(option) => option.Name || ""}
                        isOptionEqualToValue={(option, value) => option.SiteId === value.SiteId}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="All Distributor" />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.SiteId}>
                                {option.Name}
                            </li>
                        )}
                    />
                </FormControl>

            </Box>

            <Box
                sx={{
                    display: "flex",
                    gap: 3,
                    px: 3,
                }}
            >
                <Box
                    sx={{
                        flex: 4,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Card
                        sx={{
                            height: "243px",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "none",
                            border: '1px solid #E5E5E5'

                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                px: 2,
                                pt: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {/* LEFT SIDE */}
                            <Typography variant='h5' fontWeight='400'>
                                Issue Details
                            </Typography>

                            {/* RIGHT SIDE */}
                            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>

                                <ClickAwayListener onClickAway={() => setOpen(false)}>
                                    <Box>
                                        <TextField
                                            ref={anchorRef}
                                            size="small"
                                            fullWidth
                                            value={displayLabel}
                                            placeholder="Select Period"
                                            onClick={() => setOpen((prev) => !prev)}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <ArrowDropDownIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ width: '170px' }}
                                        />
                                        <Popper
                                            open={open}
                                            anchorEl={anchorRef.current}
                                            placement="bottom-start"
                                            sx={{ zIndex: 1300 }}
                                        >
                                            <Paper sx={{ p: 1, width: 170 }}>
                                                {/* OPTIONS LIST (same as Autocomplete options) */}
                                                <List dense>
                                                    {monthDdl.map((opt) => (
                                                        <ListItemButton
                                                            key={opt.Id}
                                                            selected={issueDetailsDDl?.Id === opt.Id}
                                                            onClick={() => handleIssueDetailsDdl(opt)}   // 🔑 SAME HANDLER
                                                        >
                                                            {opt.value}
                                                        </ListItemButton>
                                                    ))}
                                                </List>

                                                {/* DEFINE DATE SECTION */}
                                                {showDatePickers && (
                                                    <>
                                                        <Divider sx={{ my: 1 }} />

                                                        <Stack spacing={1}>
                                                            <FormControl size="small" fullWidth>
                                                                <FormLabel>From Date</FormLabel>
                                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                    <DesktopDatePicker
                                                                        value={fromDate}
                                                                        onChange={setFromDate}
                                                                        inputFormat="dd/MMM/yyyy"
                                                                        renderInput={(p) => (
                                                                            <TextField {...p} size="small" />
                                                                        )}
                                                                    />
                                                                </LocalizationProvider>
                                                            </FormControl>

                                                            <FormControl size="small" fullWidth>
                                                                <FormLabel>To Date</FormLabel>
                                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                    <DesktopDatePicker
                                                                        value={toDate}
                                                                        onChange={setToDate}
                                                                        inputFormat="dd/MMM/yyyy"
                                                                        renderInput={(p) => (
                                                                            <TextField {...p} size="small" />
                                                                        )}
                                                                    />
                                                                </LocalizationProvider>
                                                            </FormControl>

                                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                                <Button size="small" onClick={handleResetDate}>
                                                                    Reset
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={handleApplyDate}
                                                                >
                                                                    Apply
                                                                </Button>
                                                            </Stack>
                                                        </Stack>
                                                    </>
                                                )}
                                            </Paper>
                                        </Popper>
                                    </Box>
                                </ClickAwayListener>
                                <Tooltip title="Filter">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleIssueDetailsFilter}
                                        sx={{ minWidth: 30, height: 30 }}
                                    >
                                        <Icon icon="ic:outline-filter-alt" width="16" height="16" />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Issue tracker">
                                    <Button
                                        variant='outlined'
                                        size="small"
                                        onClick={handleClick}
                                        sx={{ minWidth: 30, height: 30 }}
                                    >
                                        <Icon icon="ic:outline-arrow-outward" width="16" height="16" />
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                px: 2,
                                pt: 1,
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                columnGap: 2,
                                rowGap: 1.5,
                            }}
                        >

                            {cardsData.map((card) => (
                                <Card
                                    key={card.id}
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        backgroundColor: card.bg,
                                        minHeight: 80, 
                                    }}
                                >

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between", // key line
                                        }}
                                    >
                                        <Typography fontWeight={500} color="#8693A6" fontSize="12px">
                                            {card.title}
                                        </Typography>

                                        <Icon
                                            icon={card.icon}
                                            width="20"
                                            height="20"
                                            style={{ color: card.iconColor }}
                                        />
                                    </Box>
                                    <Typography
                                        fontWeight={500}
                                        color="#1E293B"
                                        fontSize="26px"
                                        lineHeight={1.1}
                                        sx={{ mt: 0.5, mb: 0 }}
                                    >
                                        {card.count}
                                    </Typography>
                                    <Typography
                                        fontWeight={400}
                                        color="#94A3B8"
                                        fontSize="9.5px"
                                        lineHeight={1.2}
                                        sx={{ mt: 0 }}
                                    >
                                        {card.subTxt}
                                    </Typography>
                                </Card>
                            ))}
                        </Box>
                    </Card>

                    <Card
                        sx={{
                            height: "214px",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "none",
                            border: '1px solid #E5E5E5'
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                pt: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {/* LEFT SIDE */}
                            <Typography variant='h5' fontWeight='400'>
                                Pending Issues by lag days
                            </Typography>

                            {/* RIGHT SIDE */}

                            <Box sx={{ ml: "auto", display: "flex", gap: 1, width: 140, }}>

                                <TextField
                                    fullWidth
                                    size="small"
                                    value={lagInterval}
                                    onChange={handleLagInterval}
                                    placeholder='Lag Interval'
                                />
                                <Tooltip title="Filter">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handlePendingGraphFilter}
                                        sx={{
                                            minWidth: 30,
                                            height: 30,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Icon icon="ic:outline-filter-alt" width="16" height="16" />
                                    </Button>

                                </Tooltip>
                            </Box>

                        </Box>

                        <Box sx={{ flex: 1, px: 2, }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={lagData}
                                    layout="vertical"
                                    margin={{ top: 10, right: 40, left: 20, bottom: 10 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical horizontal={false} />

                                    {/* X Axis = Values */}
                                    <XAxis
                                        type="number"
                                        domain={[0, "dataMax"]}
                                        fontSize={12}
                                        label={{
                                            value: "No. of Issues",
                                            position: "insideBottom",
                                            dy: 10,
                                            style: {
                                                textAnchor: "middle",
                                                fontSize: 12,
                                                fontWeight: 400,
                                            },
                                        }}
                                    />


                                    {/* Y Axis = Categories */}
                                    <YAxis
                                        type="category"
                                        dataKey="lag"
                                        width={75}
                                        fontSize={12}
                                        label={{
                                            value: "Lag Slab",
                                            angle: -90,
                                            position: "insideLeft",
                                            style: {
                                                textAnchor: "middle",
                                                fontSize: 12,
                                                fontWeight: 400,
                                            },
                                        }}
                                    />

                                    <RechartsTooltip />

                                    <Bar dataKey="count" fill="#5470E3" barSize={24} isAnimationActive={false}>
                                        <LabelList dataKey="count" position="right" />
                                    </Bar>

                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Card>

                </Box>

                <Box
                    sx={{
                        flex: 6,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Card
                        sx={{
                            height: "243px",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "none",
                            border: '1px solid #E5E5E5'

                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                px: 2,
                                pt: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {/* LEFT SIDE */}
                            <Typography variant='h5' fontWeight='400'>
                                Category-wise issues
                            </Typography>

                            {/* RIGHT SIDE */}
                            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <Autocomplete
                                        size="small"
                                        id="status"
                                        value={selectedStatus.find(
                                            (item) => item.Id === status
                                        ) || null}
                                        onChange={(_, newValue) => handleStatus(newValue)}
                                        options={selectedStatus}
                                        getOptionLabel={(option) => option.Name || ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option.Id === value.Id
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} label="Status" />
                                        )}

                                    />

                                </FormControl>
                                <ClickAwayListener onClickAway={() => setOpenCategory(false)}>
                                    <Box>
                                        <TextField
                                            ref={anchorCategoryRef}
                                            size="small"
                                            fullWidth
                                            value={displayLabelCategory}
                                            placeholder="Select Period"
                                            onClick={() => setOpenCategory((prev) => !prev)}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <ArrowDropDownIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ width: '170px' }}
                                        />

                                        <Popper
                                            open={openCategory}
                                            anchorEl={anchorCategoryRef.current}
                                            placement="bottom-start"
                                            sx={{ zIndex: 1300 }}
                                        >
                                            <Paper sx={{ p: 1, width: 170 }}>
                                                {/* OPTIONS LIST (same as Autocomplete options) */}
                                                <List dense>
                                                    {monthDdl.map((opt) => (
                                                        <ListItemButton
                                                            key={opt.Id}
                                                            selected={category?.Id === opt.Id}
                                                            onClick={() => handleCategoryDDL(opt)}   // 🔑 SAME HANDLER
                                                        >
                                                            {opt.value}
                                                        </ListItemButton>
                                                    ))}
                                                </List>

                                                {/* DEFINE DATE SECTION */}
                                                {showCategoryDatePickers && (
                                                    <>
                                                        <Divider sx={{ my: 1 }} />

                                                        <Stack spacing={1}>
                                                            <FormControl size="small" fullWidth>
                                                                <FormLabel>From Date</FormLabel>
                                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                    <DesktopDatePicker
                                                                        value={fromDateCategory}
                                                                        onChange={setFromDateCategory}
                                                                        inputFormat="dd/MMM/yyyy"
                                                                        renderInput={(p) => (
                                                                            <TextField {...p} size="small" />
                                                                        )}
                                                                    />
                                                                </LocalizationProvider>
                                                            </FormControl>

                                                            <FormControl size="small" fullWidth>
                                                                <FormLabel>To Date</FormLabel>
                                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                    <DesktopDatePicker
                                                                        value={toDateCategory}
                                                                        onChange={setToDateCategory}
                                                                        inputFormat="dd/MMM/yyyy"
                                                                        renderInput={(p) => (
                                                                            <TextField {...p} size="small" />
                                                                        )}
                                                                    />
                                                                </LocalizationProvider>
                                                            </FormControl>

                                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                                <Button size="small" onClick={handleCategoryResetDate}>
                                                                    Reset
                                                                </Button>
                                                                <Button size="small" variant="contained" onClick={handleCategoryApplyDate}>
                                                                    Apply
                                                                </Button>
                                                            </Stack>
                                                        </Stack>
                                                    </>
                                                )}
                                            </Paper>
                                        </Popper>
                                    </Box>
                                </ClickAwayListener>
                                <Tooltip title="Filter">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleCategoryWiseFilter}
                                        sx={{ minWidth: 30, height: 30 }}
                                    >
                                        <Icon icon="ic:outline-filter-alt" width="16" height="16" />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Issue tracker">
                                    <Button
                                        variant='outlined'
                                        size="small"
                                        onClick={handleClick}
                                        sx={{ minWidth: 30, height: 30 }}
                                    >
                                        <Icon icon="ic:outline-arrow-outward" width="16" height="16" />
                                    </Button>
                                </Tooltip>
                            </Box>

                        </Box>
                        {hasCategoryData && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    gap: 2,
                                }}
                            >
                                {/* Pie Chart - 40% width */}
                                <Box sx={{ flex: 4, minWidth: 220, height: 250 }}>
                                    {/* Donut Chart */}
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>

                                            <Pie
                                                data={categoryWiseData}
                                                cx="50%"
                                                cy="36%"
                                                innerRadius={60}
                                                outerRadius={90}
                                                paddingAngle={2}
                                                dataKey="value"
                                                isAnimationActive={false}
                                                labelLine={false}   // ✅ remove line
                                                label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                                                    const RADIAN = Math.PI / 180;
                                                    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                                                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                    const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                                    return (
                                                        <text
                                                            x={x}
                                                            y={y}
                                                            fill="#fff"
                                                            textAnchor="middle"
                                                            dominantBaseline="central"
                                                            fontSize={12}
                                                            fontWeight={500}
                                                        >
                                                            {value}
                                                        </text>
                                                    );
                                                }}
                                            >

                                                {categoryWiseData.map((entry, index) => (
                                                    <Cell key={index} fill={entry.color} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                                {/* Right Side Legend (Custom) */}
                                <Box
                                    sx={{
                                        flex: 6,
                                        maxHeight: 250, // match pie chart height
                                        overflowY: "auto",
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        columnGap: 4,
                                        rowGap: 2,
                                        alignSelf: "center",
                                    }}
                                >
                                    {categoryWiseData.map((item, index) => (
                                        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                            <Box
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: 0.8,
                                                    backgroundColor: item.color,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography fontSize={13} color="#4A4A4A">
                                                {item.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                            </Box>)}
                        {!hasCategoryData && (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    textAlign: "center",
                                    px: 2,
                                }}
                            >
                                <Typography sx={{ color: "#928A8A", fontWeight: 500, fontSize: 16 }}>
                                    No data available
                                </Typography>
                            </Box>

                        )}

                    </Card>

                    <Card
                        sx={{
                            height: "214px",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "none",
                            border: '1px solid #E5E5E5'
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                px: 2,
                                pt: 1,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {/* LEFT SIDE */}
                            <Typography variant='h5' fontWeight='400'>
                                Month-wise Total Issues
                            </Typography>

                            {/* RIGHT SIDE */}
                            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <Autocomplete
                                        size="small"
                                        id="month"
                                        value={monthWiseDdl.find((item) => item.Id === monthDate)}
                                        onChange={(_, newValue) => handleMonthDate(newValue)}
                                        options={monthWiseDdl}
                                        getOptionLabel={(option) => option.value || ""}
                                        isOptionEqualToValue={(option, value) => option.Id === value.Id}
                                        renderInput={(params) => <TextField {...params} />}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.Id}>
                                                {option.value}
                                            </li>
                                        )}
                                    />
                                </FormControl>

                                <Tooltip title="Filter">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleMonthWiseFilter}
                                        sx={{ minWidth: 30, height: 30 }}
                                    >
                                        <Icon icon="ic:outline-filter-alt" width="16" height="16" />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Issue tracker">
                                    <Button
                                        variant='outlined' size="small"
                                        onClick={handleClick}
                                        sx={{ minWidth: 30, height: 30 }}
                                    >
                                        <Icon icon="ic:outline-arrow-outward" width="16" height="16" />
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, px: 2, }}>
                            <ResponsiveContainer width="100%" height="100%" style={{ outline: "none" }}>
                                <BarChart
                                    data={monthData}
                                    margin={{ top: 10, right: 40, left: 20, bottom: 10 }}
                                >
                                    {/* Horizontal grid only */}
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal />

                                    {/* X Axis = Months */}

                                    <XAxis
                                        dataKey="month"
                                        fontSize={12}
                                        ticks={months}

                                        label={{
                                            value: `${chartYear} Month`,
                                            position: "insideBottom",
                                            dy: 10,
                                            style: {
                                                textAnchor: "middle",
                                                fontSize: 12,
                                                fontWeight: 400,
                                            },
                                        }}
                                    />
                                    {/* Y Axis = Values */}
                                    <YAxis
                                        dataKey="count"
                                        fontSize={12}
                                        width={75}
                                        label={{
                                            value: "Count of Issue IDs",
                                            angle: -90,
                                            position: "insideLeft",
                                            style: {
                                                textAnchor: "middle",
                                                fontSize: 12,
                                                fontWeight: 400,
                                            },
                                        }}

                                    />

                                    <RechartsTooltip />
                                    <Bar dataKey="count" fill="#5470E3" barSize={24} isAnimationActive={false}>
                                        <LabelList dataKey="count" position="top" />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>

                    </Card>
                </Box>

            </Box >

            {/* ----------------------------------------------------------------- */}


            {isLoading && (<LoadingProgressBar sx={{ py: 9 }} />)}
        </Page >


    );
}